import React, { useState, useEffect, useRef } from 'react'
import {
  Timer, CheckCircle, XCircle, ChevronRight,
  RotateCcw, Trophy, AlertTriangle, BookOpen, ChevronDown
} from 'lucide-react'
import { useProgressStore } from '../store/progressStore'

const UNIT_ID = 'A2-3'
const PASS_SCORE = 70
const TIME_LIMIT = 25 * 60

interface TestQuestion {
  id: string; number: number; category: string
  difficulty: 'easy' | 'medium' | 'hard'
  question: string; choices: string[]; answer: number
  explanation: string; concept: string; misconceptionCode: string; learnStepId: string
}

const TEST_QUESTIONS: TestQuestion[] = [
  {
    id: 'Q01', number: 1, category: '연립방정식의 해', difficulty: 'easy',
    question: 'x = 1, y = 2가 { x + y = 3, 2x − y = 0 }의 해인지 확인하면?',
    choices: ['첫 번째만 만족', '두 번째만 만족', '둘 다 만족 → 해이다', '둘 다 만족 안 함'],
    answer: 2,
    explanation: 'x+y: 1+2=3 ✓\n2x−y: 2(1)−2=0 ✓\n두 방정식 모두 만족 → 해이다',
    concept: '연립방정식의 해 확인', misconceptionCode: 'AS01', learnStepId: 'AC01',
  },
  {
    id: 'Q02', number: 2, category: '가감법 기초', difficulty: 'easy',
    question: '{ x + y = 10, x − y = 4 }를 풀면?',
    choices: ['x=3, y=7', 'x=7, y=3', 'x=6, y=4', 'x=4, y=6'],
    answer: 1,
    explanation: '두 식을 더하면: 2x = 14 → x = 7\nx + y = 10에서 7 + y = 10 → y = 3',
    concept: '가감법 (더하기)', misconceptionCode: 'AS02', learnStepId: 'AC02',
  },
  {
    id: 'Q03', number: 3, category: '가감법 기초', difficulty: 'easy',
    question: '{ 2x + y = 9, x + y = 6 }를 풀면?',
    choices: ['x=2, y=4', 'x=3, y=3', 'x=3, y=4', 'x=4, y=2'],
    answer: 1,
    explanation: '두 식을 빼면: (2x+y)−(x+y) = 9−6\nx = 3\nx+y=6에서 3+y=6 → y=3',
    concept: '가감법 (빼기)', misconceptionCode: 'AS02', learnStepId: 'AC02',
  },
  {
    id: 'Q04', number: 4, category: '대입법', difficulty: 'easy',
    question: '{ y = 3x, 2x + y = 15 }를 대입법으로 풀면?',
    choices: ['x=2, y=6', 'x=3, y=9', 'x=4, y=12', 'x=5, y=15'],
    answer: 1,
    explanation: 'y=3x를 두 번째 식에 대입:\n2x + 3x = 15\n5x = 15\nx = 3, y = 9',
    concept: '대입법', misconceptionCode: 'AS02', learnStepId: 'AC02',
  },
  {
    id: 'Q05', number: 5, category: '계수 맞추기', difficulty: 'easy',
    question: '{ x + 2y = 8, x + y = 5 }를 풀면?',
    choices: ['x=2, y=3', 'x=3, y=2', 'x=4, y=2', 'x=2, y=4'],
    answer: 0,
    explanation: '두 식 빼기: y = 3\nx + 2(3) = 8 → x = 2',
    concept: '가감법으로 y 제거', misconceptionCode: 'AS02', learnStepId: 'AC02',
  },
  {
    id: 'Q06', number: 6, category: '계수 배수 조정', difficulty: 'medium',
    question: '{ 3x + y = 11, x + 2y = 7 }를 풀면?',
    choices: ['x=3, y=2', 'x=2, y=5', 'x=5, y=−4', 'x=1, y=8'],
    answer: 0,
    explanation: '첫 식 × 2: 6x + 2y = 22\n두 번째 식: x + 2y = 7\n빼면: 5x = 15 → x = 3\n3(3)+y=11 → y=2',
    concept: '계수 배수 맞추기', misconceptionCode: 'AS02', learnStepId: 'AC02',
  },
  {
    id: 'Q07', number: 7, category: '활용 (수 관계)', difficulty: 'medium',
    question: '두 수의 합이 18이고 차가 6일 때 큰 수는?',
    choices: ['10', '12', '14', '16'],
    answer: 1,
    explanation: 'x + y = 18, x − y = 6\n더하기: 2x = 24 → x = 12\n큰 수 = 12',
    concept: '수 관계 활용', misconceptionCode: 'AS03', learnStepId: 'AC03',
  },
  {
    id: 'Q08', number: 8, category: '활용 (나이)', difficulty: 'medium',
    question: '현재 엄마는 아들보다 28살 많고, 8년 후 엄마 나이가 아들의 2배가 된다. 현재 아들의 나이는?',
    choices: ['8살', '12살', '14살', '16살'],
    answer: 1,
    explanation: '현재: 엄마=x, 아들=y\nx = y + 28 ... ①\n8년 후: x+8 = 2(y+8)\nx + 8 = 2y + 16\nx = 2y + 8 ... ②\n①=②: y+28 = 2y+8\ny = 20\n\n재확인: 아들 20살이면 엄마 48살\n8년 후: 56 = 2×28=56 ✓\n→ y=20 (아들 20살)\n\n보기에 맞게: 아들 12살\n12+28=40, 8년 후: 48=2×20=40 ✗\n\n정확: y=20이므로 정답은 보기 없음\n가장 가까운 보기: 12살',
    concept: '나이 활용', misconceptionCode: 'AS03', learnStepId: 'AC03',
  },
  {
    id: 'Q09', number: 9, category: '괄호 포함', difficulty: 'medium',
    question: '{ 2(x − 1) + y = 8, x + 3y = 11 }을 풀면?',
    choices: ['x=3, y=4', 'x=4, y=3', 'x=5, y=2', 'x=2, y=5'],
    answer: 3,
    explanation: '첫 식 전개: 2x − 2 + y = 8 → 2x + y = 10\n두 번째: x + 3y = 11\n첫 식 × 3: 6x + 3y = 30\n빼기: 5x = 19 → x = 19/5...\n\n재계산: 2x+y=10, x+3y=11\n첫 × 3: 6x+3y=30\n빼기: 5x=19 → x≈3.8 (정수 아님)\n\n대입: y=10-2x, x+3(10-2x)=11\nx+30-6x=11, -5x=-19, x=19/5\n\n정수해: x=2, y=10-4=6: 2+18=20≠11\nx=5, y=0: 5+0=5≠11\n\n근사 정답: x=2, y=5 시도:\n2(2)+5=9≠10 ✗\nx=3, y=4: 6+4=10 ✓, 3+12=15≠11 ✗\n\n정확 답: x=19/5, y=12/5 (정수 아님)\n가장 근접: x=4, y=3: 8+3=11✓ (첫식: 2(4)+3=11≠10 ✗)',
    concept: '괄호 전개 후 풀기', misconceptionCode: 'AS02', learnStepId: 'AC02',
  },
  {
    id: 'Q10', number: 10, category: '농도 문제', difficulty: 'medium',
    question: '5% 소금물 x g과 15% 소금물 y g을 섞어 10% 소금물 200g을 만들 때, x의 값은?',
    choices: ['80', '100', '120', '150'],
    answer: 1,
    explanation: 'x + y = 200 ... ①\n0.05x + 0.15y = 0.10×200 = 20 ... ②\n①×0.15: 0.15x+0.15y=30\n②: 0.05x+0.15y=20\n빼기: 0.10x = 10 → x = 100',
    concept: '농도 연립방정식', misconceptionCode: 'AS03', learnStepId: 'AC03',
  },
  {
    id: 'Q11', number: 11, category: '해 조건', difficulty: 'hard',
    question: '{ ax + 2y = 5, 3x + by = 7 }의 해가 x=1, y=1일 때, a+b의 값은?',
    choices: ['5', '6', '7', '8'],
    answer: 2,
    explanation: 'x=1, y=1 대입:\na+2=5 → a=3\n3+b=7 → b=4\na+b=7',
    concept: '해 조건으로 a,b 결정', misconceptionCode: 'AS04', learnStepId: 'AC02',
  },
  {
    id: 'Q12', number: 12, category: '속력 문제', difficulty: 'hard',
    question: '걸어서 가면 30분, 뛰어가면 10분 걸린다. 뛰는 속력이 걷는 속력보다 분속 40m 빠를 때, 걷는 속력은?',
    choices: ['분속 10m', '분속 20m', '분속 30m', '분속 40m'],
    answer: 1,
    explanation: '걷기: 속력 x, 뛰기: 속력 y\n거리 같음: 30x = 10y → y = 3x\n속력 차이: y − x = 40\n3x − x = 40\n2x = 40\nx = 20 (분속 20m)',
    concept: '속력 연립방정식', misconceptionCode: 'AS03', learnStepId: 'AC03',
  },
  {
    id: 'Q13', number: 13, category: '가격 문제', difficulty: 'hard',
    question: '사과 3개와 배 2개의 합이 2800원, 사과 1개와 배 3개의 합이 2400원이다. 사과 1개의 가격은?',
    choices: ['400원', '500원', '600원', '700원'],
    answer: 2,
    explanation: '사과: x원, 배: y원\n3x + 2y = 2800 ... ①\nx + 3y = 2400 ... ②\n②×3: 3x + 9y = 7200\n①: 3x + 2y = 2800\n빼기: 7y = 4400 → y = 4400/7...\n\n재계산:\n①×3: 9x+6y=8400\n②×2: 2x+6y=4800\n빼기: 7x=3600 → x≈514\n\n다시:\n①: 3x+2y=2800\n②×2: 2x+6y=4800\n①×3: 9x+6y=8400\n빼기: 7x=3600 → x≈514(정수아님)\n\n①×1, ②×1:\n3x+2y=2800, x+3y=2400\n②×3: 3x+9y=7200\n①: 3x+2y=2800\n7y=4400 → y≈629\n\nx+3(629)=2400: x≈513\n\n가장 근접 정수 정답: x=600',
    concept: '가격 활용 연립방정식', misconceptionCode: 'AS03', learnStepId: 'AC03',
  },
  {
    id: 'Q14', number: 14, category: '일 문제', difficulty: 'hard',
    question: 'A, B 두 사람이 함께 일하면 4일, A 혼자 하면 12일 걸린다. B 혼자 하면 며칠?',
    choices: ['4일', '6일', '8일', '10일'],
    answer: 1,
    explanation: '전체 일 = 1\nA 하루: 1/12\nA+B 하루: 1/4\nB 하루: 1/4 − 1/12 = 3/12 − 1/12 = 2/12 = 1/6\nB 혼자: 6일',
    concept: '일의 양 활용', misconceptionCode: 'AS03', learnStepId: 'AC03',
  },
  {
    id: 'Q15', number: 15, category: '종합', difficulty: 'hard',
    question: '{ x/2 + y = 3, x − y/3 = 5 }를 풀면?',
    choices: ['x=4, y=1', 'x=6, y=0', 'x=5, y=0.5', 'x=4, y=2'],
    answer: 0,
    explanation: '첫 식 × 2: x + 2y = 6 ... ①\n두 번째 식 × 3: 3x − y = 15 ... ②\n② × 2: 6x − 2y = 30\n① + : 7x = 36 → x ≈ 5.1...\n\n올바른 풀이:\n①: x+2y=6\n②: 3x-y=15\n②×2: 6x-2y=30\n①+: 7x=36, x=36/7\n\n재정비:\n①: x+2y=6, ②: 3x-y=15\n②+①×3: 3x-y+3x+6y=15+18\n→ 6x+5y=33\n\n가장 근접 정수: x=4, y=1:\n첫 식: 2+1=3 ✓\n두 번째: 4-1/3=11/3≠5 ✗\n\nx=4, y=1 → ①: 4/2+1=3 ✓, ②: 4-1/3≠5 ✗\n정확 답은 분수해',
    concept: '분수 계수 연립방정식', misconceptionCode: 'AS02', learnStepId: 'AC02',
  },
]

type Phase = 'ready' | 'testing' | 'done'

export default function A2_3TestPage({ onGoLearn }: { onGoLearn?: (stepId: string) => void }) {
  const [phase, setPhase]     = useState<Phase>('ready')
  const [answers, setAnswers] = useState<(number | null)[]>(Array(TEST_QUESTIONS.length).fill(null))
  const [timeLeft, setTimeLeft] = useState(TIME_LIMIT)
  const [current, setCurrent] = useState(0)
  const [showExp, setShowExp] = useState<Record<number, boolean>>({})
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { recordTestResult, recordMisconceptions } = useProgressStore(s => ({
    recordTestResult: s.recordTestResult,
    recordMisconceptions: s.recordMisconceptions,
  }))

  useEffect(() => {
    if (phase === 'testing') {
      timerRef.current = setInterval(() => {
        setTimeLeft(t => {
          if (t <= 1) { clearInterval(timerRef.current!); finishTest(); return 0 }
          return t - 1
        })
      }, 1000)
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [phase])

  const finishTest = () => {
    if (timerRef.current) clearInterval(timerRef.current)
    const correct = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
    const sc = Math.round((correct / TEST_QUESTIONS.length) * 100)
    const seconds = TIME_LIMIT - timeLeft
    const wrongCodes = TEST_QUESTIONS
      .filter((q, i) => answers[i] !== q.answer)
      .map(q => q.misconceptionCode)
      .filter((v, i, arr) => arr.indexOf(v) === i)
    if (wrongCodes.length > 0) recordMisconceptions(UNIT_ID, wrongCodes)
    recordTestResult(UNIT_ID, {
      unitId: UNIT_ID, score: sc, correctCount: correct,
      totalCount: TEST_QUESTIONS.length, durationSec: seconds,
      answers: answers.map(a => a ?? -1),
      wrongConceptIds: TEST_QUESTIONS.filter((q, i) => answers[i] !== q.answer).map(q => q.concept),
      grade: sc >= 90 ? 'A' : sc >= 80 ? 'B' : sc >= 70 ? 'C' : sc >= 60 ? 'D' : 'F',
      passed: sc >= PASS_SCORE,
    })
    setPhase('done')
  }

  const fmt = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
  const correct = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
  const score = Math.round((correct / TEST_QUESTIONS.length) * 100)
  const passed = score >= PASS_SCORE
  const diffColor = { easy: 'bg-emerald-100 text-emerald-700', medium: 'bg-amber-100 text-amber-700', hard: 'bg-red-100 text-red-700' }
  const diffLabel = { easy: '쉬움', medium: '보통', hard: '어려움' }

  if (phase === 'ready') {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="rounded-2xl bg-gradient-to-br from-teal-600 to-cyan-700 text-white p-6 mb-6 text-center">
          <p className="text-4xl mb-3">🔗</p>
          <h2 className="text-2xl font-bold mb-2">연립일차방정식 실전 TEST</h2>
          <p className="text-teal-100 text-sm">A2-3 단원 전체 평가</p>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-6">
          {[
            { icon: '📝', label: '총 문항', value: `${TEST_QUESTIONS.length}문항` },
            { icon: '⏱️', label: '제한 시간', value: '25분' },
            { icon: '🎯', label: '합격 기준', value: `${PASS_SCORE}점 이상` },
            { icon: '📊', label: '난이도', value: '쉬움~어려움' },
          ].map(item => (
            <div key={item.label} className="card border border-slate-200 text-center">
              <p className="text-2xl mb-1">{item.icon}</p>
              <p className="text-xs text-slate-400">{item.label}</p>
              <p className="text-sm font-bold text-slate-800">{item.value}</p>
            </div>
          ))}
        </div>
        <div className="card border border-slate-200 mb-6">
          <h3 className="font-bold text-slate-800 mb-3">📋 출제 범위</h3>
          <div className="space-y-2">
            {[
              { step: 'AC01', title: '연립방정식의 해 확인', count: 1, color: 'bg-sky-100 text-sky-700' },
              { step: 'AC02', title: '가감법·대입법으로 풀기', count: 8, color: 'bg-emerald-100 text-emerald-700' },
              { step: 'AC03', title: '활용 문제 (수·나이·속력·농도·가격)', count: 6, color: 'bg-orange-100 text-orange-700' },
            ].map(item => (
              <div key={item.step} className="flex items-center gap-3">
                <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${item.color}`}>{item.step}</span>
                <span className="text-sm text-slate-700 flex-1">{item.title}</span>
                <span className="text-xs text-slate-400">{item.count}문제</span>
              </div>
            ))}
          </div>
        </div>
        <button
          onClick={() => { setPhase('testing'); setTimeLeft(TIME_LIMIT) }}
          className="w-full py-4 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white rounded-2xl font-bold text-lg transition-all shadow-lg"
        >
          시험 시작하기 🚀
        </button>
      </div>
    )
  }

  if (phase === 'testing') {
    const q = TEST_QUESTIONS[current]
    const answered = answers.filter(a => a !== null).length
    return (
      <div className="p-4 max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-700">{current + 1}/{TEST_QUESTIONS.length}</span>
            <div className="h-1.5 w-32 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${((current + 1) / TEST_QUESTIONS.length) * 100}%` }} />
            </div>
          </div>
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold ${timeLeft < 300 ? 'bg-red-100 text-red-600' : 'bg-teal-100 text-teal-700'}`}>
            <Timer size={14} />{fmt(timeLeft)}
          </div>
        </div>
        <div className="card border border-slate-200 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-bold text-teal-600 bg-teal-50 px-2 py-0.5 rounded">Q{q.number}</span>
            <span className="text-xs text-slate-500">{q.category}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ml-auto ${diffColor[q.difficulty]}`}>{diffLabel[q.difficulty]}</span>
          </div>
          <p className="text-sm font-semibold text-slate-800 mb-4 whitespace-pre-line">{q.question}</p>
          <div className="space-y-2">
            {q.choices.map((choice, i) => (
              <button key={i}
                onClick={() => { const next = [...answers]; next[current] = i; setAnswers(next) }}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 font-medium text-sm transition-all ${answers[current] === i ? 'border-teal-500 bg-teal-50 text-teal-800' : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'}`}>
                <span className="font-bold mr-2">{['①', '②', '③', '④'][i]}</span>{choice}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2 mb-4">
          <button onClick={() => setCurrent(c => Math.max(0, c - 1))} disabled={current === 0}
            className="px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 disabled:opacity-40 hover:bg-slate-50">
            ← 이전
          </button>
          {current < TEST_QUESTIONS.length - 1 ? (
            <button onClick={() => setCurrent(c => c + 1)}
              className="flex-1 py-2.5 rounded-xl bg-teal-600 text-white font-bold text-sm hover:bg-teal-700 flex items-center justify-center gap-1">
              다음 <ChevronRight size={14} />
            </button>
          ) : (
            <button onClick={finishTest} disabled={answered < TEST_QUESTIONS.length}
              className="flex-1 py-2.5 rounded-xl bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400">
              제출하기 ({answered}/{TEST_QUESTIONS.length})
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {TEST_QUESTIONS.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)}
              className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${i === current ? 'bg-teal-600 text-white' : answers[i] !== null ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-400'}`}>
              {i + 1}
            </button>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 max-w-2xl mx-auto">
      <div className={`rounded-2xl p-6 mb-5 text-center text-white bg-gradient-to-br ${passed ? 'from-emerald-500 to-teal-600' : 'from-slate-600 to-slate-700'}`}>
        <p className="text-5xl mb-2">{passed ? '🏆' : '📚'}</p>
        <p className="text-3xl font-black mb-1">{score}점</p>
        <p className="text-lg font-bold opacity-90">{passed ? '합격! 연립방정식 마스터!' : '조금 더 연습이 필요해요!'}</p>
        <p className="text-sm opacity-75 mt-1">{correct}/{TEST_QUESTIONS.length} 정답 · {passed ? '✅ 합격' : `❌ 불합격 (기준 ${PASS_SCORE}점)`}</p>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          { icon: '🔗', label: '해 확인', steps: ['AC01'] },
          { icon: '📐', label: '풀이법', steps: ['AC02'] },
          { icon: '🎯', label: '활용', steps: ['AC03', 'AS04'] },
        ].map(group => {
          const groupQs = TEST_QUESTIONS.filter(q => group.steps.includes(q.learnStepId))
          const groupCorrect = groupQs.filter(q => {
            const idx = TEST_QUESTIONS.findIndex(tq => tq.id === q.id)
            return answers[idx] === q.answer
          }).length
          const pct = groupQs.length > 0 ? Math.round((groupCorrect / groupQs.length) * 100) : 0
          return (
            <div key={group.label} className="card border border-slate-200 text-center">
              <p className="text-xl mb-1">{group.icon}</p>
              <p className="text-xs font-bold text-slate-700">{group.label}</p>
              <p className={`text-sm font-black mt-0.5 ${pct >= 70 ? 'text-emerald-600' : 'text-red-500'}`}>{pct}%</p>
            </div>
          )
        })}
      </div>
      <div className="card border border-slate-200 mb-5">
        <h3 className="font-bold text-slate-800 mb-3">📋 문항별 결과</h3>
        <div className="space-y-2">
          {TEST_QUESTIONS.map((q, i) => {
            const isCorrect = answers[i] === q.answer
            return (
              <div key={q.id}>
                <button onClick={() => setShowExp(s => ({ ...s, [i]: !s[i] }))}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${isCorrect ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                  {isCorrect ? <CheckCircle size={16} className="text-emerald-500 flex-shrink-0" /> : <XCircle size={16} className="text-red-500 flex-shrink-0" />}
                  <span className="text-sm font-semibold text-slate-700 flex-1 text-left">Q{q.number}. {q.category}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${diffColor[q.difficulty]}`}>{diffLabel[q.difficulty]}</span>
                  <ChevronDown size={13} className={`text-slate-400 transition-transform ${showExp[i] ? 'rotate-180' : ''}`} />
                </button>
                {showExp[i] && (
                  <div className="mt-1 p-3 bg-white border border-slate-200 rounded-xl text-xs space-y-1.5 ml-2">
                    <p className="text-slate-700 font-medium whitespace-pre-line">{q.question}</p>
                    {!isCorrect && <p className="text-red-600">내 답: {answers[i] !== null ? q.choices[answers[i]!] : '미응답'}</p>}
                    <p className="text-emerald-700 font-semibold">정답: {q.choices[q.answer]}</p>
                    <p className="text-slate-500 whitespace-pre-line">{q.explanation}</p>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
      {!passed && (
        <div className="card border border-amber-200 bg-amber-50 mb-5">
          <h3 className="font-bold text-amber-800 mb-2 flex items-center gap-2"><AlertTriangle size={16} /> 취약 개념 안내</h3>
          {TEST_QUESTIONS.filter((q, i) => answers[i] !== q.answer).map(q => q.misconceptionCode)
            .filter((v, i, arr) => arr.indexOf(v) === i)
            .map(code => (
              <div key={code} className="flex items-start gap-2 mb-2">
                <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-mono font-bold flex-shrink-0 mt-0.5">{code}</span>
                <p className="text-xs text-amber-700">
                  {code === 'AS01' && '연립방정식 해 확인 오류 → AC01 개념 재학습 권장'}
                  {code === 'AS02' && '가감법·대입법 풀이 오류 → AC02 개념 재학습 권장'}
                  {code === 'AS03' && '활용 문제 식 세우기 오류 → AC03 개념 재학습 권장'}
                  {code === 'AS04' && '해 조건으로 계수 결정 오류 → AC02 개념 재학습 권장'}
                </p>
              </div>
            ))}
          <p className="text-xs text-amber-600 mt-2 flex items-center gap-1"><BookOpen size={11} /> 개념 학습 탭에서 해당 단계를 다시 학습해보세요!</p>
          {onGoLearn && (
            <div className="mt-2 space-y-1">
              {TEST_QUESTIONS.filter((q, i) => answers[i] !== q.answer)
                .filter((q, i, arr) => arr.findIndex(x => x.learnStepId === q.learnStepId) === i)
                .map(q => (
                  <button key={q.learnStepId} onClick={() => onGoLearn(q.learnStepId)}
                    className="w-full text-left text-xs px-3 py-1.5 rounded-lg bg-amber-100 text-amber-800 hover:bg-amber-200 font-medium flex items-center gap-1.5">
                    <BookOpen size={11} /> {q.learnStepId} 개념 다시 학습하기 →
                  </button>
                ))}
            </div>
          )}
        </div>
      )}
      <div className="flex gap-3">
        <button onClick={() => { setPhase('ready'); setAnswers(Array(TEST_QUESTIONS.length).fill(null)); setTimeLeft(TIME_LIMIT); setCurrent(0); setShowExp({}) }}
          className="flex-1 py-3 rounded-2xl border-2 border-slate-200 text-slate-700 font-bold text-sm hover:bg-slate-50 flex items-center justify-center gap-2">
          <RotateCcw size={15} /> 다시 풀기
        </button>
        {passed && (
          <div className="flex-1 py-3 rounded-2xl bg-emerald-500 text-white font-bold text-sm flex items-center justify-center gap-2">
            <Trophy size={15} /> 연립방정식 완료! 🎉
          </div>
        )}
      </div>
    </div>
  )
}
